import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { DownloadSimple, Printer, ArrowLeft, ArrowRight, Clock } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const FLAG_TONE = {
  open_session: ["Open session", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  missing_checkout: ["Missing check-out", "bg-red-50 text-red-600 border-red-200"],
  late_arrival: ["Late arrival", "bg-amber-50 text-amber-700 border-amber-200"],
  late_pickup: ["Late pickup", "bg-red-50 text-red-600 border-red-200"],
  absence: ["Absence", "bg-slate-100 text-slate-600 border-slate-200"],
  corrected: ["Corrected", "bg-purple-50 text-purple-700 border-purple-200"],
  voided: ["Voided", "bg-slate-200 text-slate-600 border-slate-300"],
  multiple_sessions: ["Multiple sessions", "bg-blue-50 text-blue-700 border-blue-200"],
};
const EVENT_TONE = { in: "text-emerald-700", out: "text-blue-700", absent: "text-red-600" };
const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

export default function AttendanceLog() {
  const [q, setQ] = useState({ start: daysAgo(14), end: today(), student_id: "all", room: "all",
                               event_type: "all", include_voided: true });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [students, setStudents] = useState([]);
  const [busy, setBusy] = useState(false);

  const params = useMemo(() => ({
    start: q.start, end: q.end, event_type: q.event_type, include_voided: q.include_voided,
    ...(q.student_id !== "all" ? { student_id: q.student_id } : {}),
    ...(q.room !== "all" ? { room: q.room } : {}),
  }), [q]);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/attendance/log", { params: { ...params, page, limit: 200 } });
      setData(d);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [params, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      try {
        const [r, s] = await Promise.all([api.get("/rooms"), api.get("/students/directory")]);
        setRooms(r.data); setStudents(s.data);
      } catch { /* filters stay as free range when a list is unavailable */ }
    })();
  }, []);

  const set = (k, v) => { setPage(1); setQ((prev) => ({ ...prev, [k]: v })); };

  const download = async () => {
    setBusy(true);
    try {
      const { data: blob } = await api.get("/attendance/log", { params: { ...params, format: "csv" },
                                                               responseType: "blob" });
      const url = URL.createObjectURL(new Blob([blob], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance_log_${q.start}_to_${q.end}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded with every filtered event");
    } catch (e) {
      const detail = e.response?.data instanceof Blob ? await e.response.data.text() : e.response?.data?.detail;
      toast.error(apiError(typeof detail === "string" && detail.startsWith("{") ? JSON.parse(detail).detail : detail));
    }
    setBusy(false);
  };

  const s = data?.summary;
  return (
    <div data-testid="attendance-log-page">
      <div className="print:hidden">
        <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Attendance records</p>
        <h1 className="mt-3 text-4xl font-bold tracking-tight">Check-in &amp; check-out history</h1>
        <p className="mt-2 text-sm text-muted-foreground max-w-3xl">
          Every arrival and departure event, including more than one pair in a day. Nothing is collapsed into
          a first arrival and last departure. <Link to="/admin/attendance" className="underline font-semibold">Back to the check-in board</Link>
        </p>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-border p-5 flex flex-wrap gap-4 items-end print:hidden" data-testid="log-filters">
        <div><Label className="text-xs font-bold">From</Label>
          <Input type="date" value={q.start} onChange={(e) => set("start", e.target.value)}
                 className="mt-1 h-11 rounded-xl w-40" data-testid="log-start" /></div>
        <div><Label className="text-xs font-bold">To</Label>
          <Input type="date" value={q.end} onChange={(e) => set("end", e.target.value)}
                 className="mt-1 h-11 rounded-xl w-40" data-testid="log-end" /></div>
        <div><Label className="text-xs font-bold">Student</Label>
          <Select value={q.student_id} onValueChange={(v) => set("student_id", v)}>
            <SelectTrigger className="mt-1 h-11 rounded-xl w-52" data-testid="log-student"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All students</SelectItem>
              {students.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-xs font-bold">Classroom</Label>
          <Select value={q.room} onValueChange={(v) => set("room", v)}>
            <SelectTrigger className="mt-1 h-11 rounded-xl w-48" data-testid="log-room"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All classrooms</SelectItem>
              {rooms.map((x) => <SelectItem key={x.id} value={x.room}>{x.room}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-xs font-bold">Event</Label>
          <Select value={q.event_type} onValueChange={(v) => set("event_type", v)}>
            <SelectTrigger className="mt-1 h-11 rounded-xl w-44" data-testid="log-event-type"><SelectValue /></SelectTrigger>
            <SelectContent>{[["all", "All events"], ["in", "Check-ins"], ["out", "Check-outs"], ["absent", "Absences"]]
              .map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select></div>
        <label className="flex items-center gap-2 text-sm font-semibold h-11">
          <input type="checkbox" checked={q.include_voided} onChange={(e) => set("include_voided", e.target.checked)}
                 data-testid="log-include-voided" /> Include voided
        </label>
        <div className="ml-auto flex gap-2">
          {data?.can_export && (
            <Button onClick={download} disabled={busy} variant="outline" className="h-11 rounded-full font-bold"
                    data-testid="log-export-btn"><DownloadSimple size={16} className="mr-2" />{busy ? "Preparing…" : "Download CSV"}</Button>
          )}
          <Button onClick={() => window.print()} className="h-11 rounded-full bg-[#0F172A] hover:bg-slate-800 font-bold"
                  data-testid="log-print-btn"><Printer size={16} className="mr-2" /> Printable report</Button>
        </div>
      </div>

      {!data ? <p className="mt-8 text-muted-foreground" data-testid="log-loading">Loading attendance history…</p> : (
        <>
          <div className="mt-6 hidden print:block">
            <h2 className="text-2xl font-bold">Check-in &amp; check-out report</h2>
            <p className="text-sm">{data.filters.start} to {data.filters.end}
              {q.room !== "all" ? ` · ${q.room}` : ""}{q.student_id !== "all" ? ` · one student` : ""}</p>
          </div>
          <p className="mt-4 text-xs font-semibold text-muted-foreground flex items-center gap-2" data-testid="log-meta">
            <Clock size={14} /> All times {data.time_zone_label} ({data.time_zone}) · report generated {new Date(data.generated_at).toLocaleString()}
          </p>

          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="log-summary">
            {[["Events", s.events], ["Check-ins", s.check_ins], ["Check-outs", s.check_outs], ["Absences", s.absences],
              ["Open sessions", s.open_sessions], ["Missing check-outs", s.missing_checkouts],
              ["Late arrivals", s.late_arrivals], ["Late pickups", s.late_pickups]].map(([k, v]) => (
              <div key={k} className="bg-white rounded-2xl border border-border p-4"
                   data-testid={`log-stat-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{k}</p>
                <p className="mt-1 font-display text-2xl font-bold">{v}</p>
              </div>
            ))}
          </div>

          {data.truncated && (
            <p className="mt-4 text-sm font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3 print:hidden"
               data-testid="log-truncation-notice">
              Showing events {(data.page - 1) * data.limit + 1}–{(data.page - 1) * data.limit + data.showing} of {data.total}.
              The CSV export contains all {data.total} filtered events.
            </p>
          )}

          <div className="mt-4 bg-white rounded-2xl border border-border overflow-x-auto" data-testid="log-table">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-xs uppercase tracking-wider">
                {["Date", "Time", "Student", "Classroom at the time", "Event", "Recorded by", "Guardian / pickup",
                  "Session", "In care", "Flags"].map((h) => (
                  <th key={h} className="text-left p-3 font-bold whitespace-nowrap">{h}</th>))}
              </tr></thead>
              <tbody>
                {data.rows.length === 0 && (
                  <tr><td colSpan={10} className="p-6 text-muted-foreground" data-testid="log-empty">
                    No check-in or check-out events match these filters.</td></tr>
                )}
                {data.rows.map((x) => (
                  <tr key={x.id} className={`border-b border-border last:border-0 ${x.voided ? "opacity-60" : ""}`}
                      data-testid={`log-row-${x.id}`}>
                    <td className="p-3 font-bold whitespace-nowrap">{x.date}<span className="ml-1 text-muted-foreground font-semibold">{x.weekday}</span></td>
                    <td className="p-3 whitespace-nowrap font-semibold">{x.time}</td>
                    <td className="p-3 whitespace-nowrap">
                      <Link to={`/admin/students/${x.student_id}?tab=attendance`} className="font-bold hover:text-[#3B82F6]"
                            data-testid={`log-student-${x.id}`}>{x.student_name}</Link>
                    </td>
                    <td className="p-3 whitespace-nowrap">{x.classroom || "—"}</td>
                    <td className={`p-3 font-bold whitespace-nowrap ${EVENT_TONE[x.kind]}`}>{x.event}
                      {x.absence_reason && <span className="block text-xs font-semibold text-muted-foreground">{x.absence_reason}</span>}</td>
                    <td className="p-3 whitespace-nowrap">{x.recorded_by || "—"}
                      <span className="block text-xs text-muted-foreground">{x.source_label}</span></td>
                    <td className="p-3 whitespace-nowrap">{x.guardian || "—"}</td>
                    <td className="p-3 whitespace-nowrap">{x.session ? `#${x.session} ${x.session_state}` : "—"}</td>
                    <td className="p-3 whitespace-nowrap">{x.minutes_in_care
                      ? `${Math.floor(x.minutes_in_care / 60)}h ${x.minutes_in_care % 60}m` : "—"}</td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {x.flags.map((f) => FLAG_TONE[f] && (
                          <span key={f} className={`text-xs font-bold px-2 py-0.5 rounded-full border ${FLAG_TONE[f][1]}`}
                                data-testid={`log-flag-${f}-${x.id}`}>{FLAG_TONE[f][0]}</span>
                        ))}
                      </div>
                      {x.corrected && x.corrections?.length > 0 && (
                        <p className="mt-1 text-xs text-purple-700 font-semibold" data-testid={`log-correction-${x.id}`}>
                          {x.corrections.map((c) => `${c.by} · ${(c.at || "").slice(0, 10)} · ${c.reason}`).join(" | ")}
                        </p>
                      )}
                      {x.voided && (
                        <p className="mt-1 text-xs text-slate-600 font-semibold">Voided by {x.voided_by}: {x.void_reason}</p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.pages > 1 && (
            <div className="mt-4 flex items-center gap-3 print:hidden" data-testid="log-pagination">
              <Button variant="outline" className="rounded-full font-bold" disabled={page <= 1}
                      onClick={() => setPage(page - 1)} data-testid="log-prev"><ArrowLeft size={15} /></Button>
              <span className="text-sm font-semibold">Page {data.page} of {data.pages}</span>
              <Button variant="outline" className="rounded-full font-bold" disabled={page >= data.pages}
                      onClick={() => setPage(page + 1)} data-testid="log-next"><ArrowRight size={15} /></Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
